// Lumensurge level-up overlay: 3 upgrade cards + reroll, touch, mouse and
// keyboard (1/2/3 select, R reroll). Mobile-first: below 768px the cards are
// full-width row buttons in a single-column grid; at 768px+ they become the
// classic 3-column card row with a TAKE footer. Cards follow the v3
// wireframe: slim category/rarity strip on top, framed icon shrine, name,
// description. Pixel-bordered panels + Silkscreen, matching the HUD/menu
// theme in theme.ts. While open it holds the sim paused via the provided
// setPaused callback.

import type { UpgradeChoice, UpgradeRarity } from '../sim/upgrade-system.js';
import { colors, ensureUiStyles, FONT_UI, icon, pixelShadow } from './theme.js';
import { BOON_CATEGORY_META, categorizeUpgrade } from './boon-categories.js';
import { makeHapticSwitch } from '../input/haptics.js';

export interface UpgradeOverlayCallbacks {
  /** Called with the chosen card index (0..2). */
  onPick(index: number): void;
  /** Called when the player requests a reroll. */
  onReroll(): void;
  /** Pause/resume the simulation while the overlay is open. */
  onSetPaused(paused: boolean): void;
}

export interface UpgradeOverlayShowOptions {
  /**
   * When set, only choices with one of these rarities are shown — e.g. the
   * wormhole bonus game's boon choice offers rare/mythical only. Picks are
   * still reported as indices into the original `choices` array, so the
   * sim's offer ordering is unaffected. If filtering would leave nothing,
   * the full offer is shown so the player is never soft-locked.
   */
  filterRarities?: readonly UpgradeRarity[];
}

const STYLE_ID = 'la-upgrade-styles';

function ensureOverlayStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
/* Lumen Abyss level-up overlay — see src/ui/upgrade-overlay.ts.
   Mobile-first: single-column full-width cards, stepping up to a 3-column
   card row at 768px and larger cards at 1600px. */
.la-upgrade-root {
  position: absolute; inset: 0; display: none; box-sizing: border-box;
  align-items: center; justify-content: center;
  background: rgba(5, 10, 20, 0.6);
  z-index: 50;
  font-family: ${FONT_UI}; color: ${colors.text}; font-size: 11px;
  text-shadow: 0 2px 0 rgba(0, 0, 0, 0.7);
  padding: env(safe-area-inset-top) env(safe-area-inset-right)
           env(safe-area-inset-bottom) env(safe-area-inset-left);
}
.la-upgrade-panel {
  display: flex; flex-direction: column; align-items: stretch; gap: 12px;
  box-sizing: border-box;
  background: ${colors.panelBg}; box-shadow: ${pixelShadow(colors.panelBorder, 4)};
  padding: 18px 16px; width: min(420px, 100%);
  max-width: calc(100vw - 24px);
  max-width: calc(100vw - 24px - env(safe-area-inset-left) - env(safe-area-inset-right));
  max-height: calc(100vh - 24px);
  max-height: calc(100vh - 24px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  max-height: calc(100dvh - 24px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  overflow-y: auto; overscroll-behavior: contain; pointer-events: auto;
}
.la-upgrade-title {
  margin: 0; font-size: 13px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  /* Mobile: solid gold band (wireframe v4); desktop strips it to gold text. */
  background: ${colors.accent}; color: ${colors.barTrack};
  padding: 5px 18px; letter-spacing: 3px; text-align: center;
}
.la-upgrade-subtitle {
  margin-top: -6px; text-align: center; font-size: 8px;
  letter-spacing: 2px; color: ${colors.muted};
}
.la-upgrade-cards { display: grid; grid-template-columns: 1fr; gap: 10px; }
.la-upgrade-card {
  display: flex; flex-direction: column; align-items: stretch; text-align: left;
  width: 100%; min-height: 44px; padding: 0; box-sizing: border-box;
  background: ${colors.barTrack}; color: ${colors.text};
  border: 0; border-radius: 0; box-shadow: ${pixelShadow()};
  font-family: inherit; font-size: inherit;
  cursor: pointer; pointer-events: auto; overflow: hidden;
  touch-action: manipulation; user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.la-upgrade-card:hover, .la-upgrade-card:focus-visible {
  box-shadow: ${pixelShadow(colors.accent)}; color: ${colors.accent}; outline: none;
}
.la-upgrade-card.rarity-common { box-shadow: ${pixelShadow('#8fb3cc')}; }
.la-upgrade-card.rarity-rare { box-shadow: ${pixelShadow('#4db8ff')}; }
.la-upgrade-card.rarity-mythical { box-shadow: ${pixelShadow('#c86bff')}; }
/* Slim strip (wireframe v3): category left, rarity spelled out right,
   tinted with the rarity color. Replaces the old thick band. */
.la-up-strip {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 5px 8px; font-size: 8px; letter-spacing: 1px; line-height: 1;
}
.la-upgrade-card.rarity-common .la-up-strip { color: #8fb3cc; background: rgba(143, 179, 204, 0.22); }
.la-upgrade-card.rarity-rare .la-up-strip { color: #4db8ff; background: rgba(77, 184, 255, 0.30); }
.la-upgrade-card.rarity-mythical .la-up-strip { color: #c86bff; background: rgba(200, 107, 255, 0.30); }
.la-up-cat { display: inline-flex; align-items: center; gap: 4px; }
.la-up-rar { display: none; letter-spacing: 2px; }
/* Mobile (wireframe v4): the boon name and the ordinal key live in the
   strip; rarity is conveyed by the strip's color alone. */
.la-up-strip-name {
  flex: 1; min-width: 0; font-weight: 700; letter-spacing: 1px;
  color: ${colors.text};
}
.la-up-strip-key {
  flex: none; padding: 2px 6px; font-size: 9px;
  box-shadow: inset 0 0 0 2px currentColor; color: inherit;
  display: inline-block; min-width: 2.2ch; text-align: center;
}
/* Main: mobile row — framed icon box left, name + description right. */
.la-up-main {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; box-sizing: border-box; flex: 1;
}
.la-up-iconbox {
  position: relative;
  flex: none; width: 64px; height: 64px;
  display: flex; align-items: center; justify-content: center;
  color: ${colors.text}; font-size: 28px;
}
/* Generated boon art bare — no frame; shown 1:1 at the 64px style-guide
   master size so every pixel reads. Sits over the hn glyph fallback. */
.la-up-iconimg {
  position: absolute; inset: 0; margin: auto;
  width: 64px; height: 64px; object-fit: contain;
  image-rendering: pixelated;
}
/* Rarity reads as a soft aura on the art itself now that the frame is gone. */
.la-upgrade-card.rarity-rare .la-up-iconimg { filter: drop-shadow(0 0 5px rgba(77, 184, 255, 0.65)); }
.la-upgrade-card.rarity-mythical .la-up-iconimg { filter: drop-shadow(0 0 6px rgba(200, 107, 255, 0.7)); }
.la-up-texts { display: flex; flex-direction: column; gap: 5px; min-width: 0; text-align: left; }
.la-up-name { display: none; font-weight: 700; letter-spacing: 1px; line-height: 1.35; }
.la-up-desc { color: ${colors.muted}; font-size: 9px; line-height: 1.5; }
.la-upgrade-card:hover .la-up-desc,
.la-upgrade-card:focus-visible .la-up-desc { color: ${colors.text}; }
/* TAKE footer: desktop only — on mobile the whole card is the tap target. */
.la-up-foot { display: none; }
.la-upgrade-key {
  padding: 2px 6px; font-size: 9px;
  box-shadow: inset 0 0 0 2px ${colors.panelBorder}; color: ${colors.text};
  display: inline-block; min-width: 2.2ch; text-align: center;
}

/* Appearance and selection animations */
.la-upgrade-card {
  opacity: 0; transform: translateY(8px);
  animation: la-card-in 0.25s ease-out forwards;
}
.la-upgrade-card:nth-child(1) { animation-delay: 0.05s; }
.la-upgrade-card:nth-child(2) { animation-delay: 0.1s; }
.la-upgrade-card:nth-child(3) { animation-delay: 0.15s; }
@keyframes la-card-in {
  to { opacity: 1; transform: translateY(0); }
}
.la-upgrade-card.selected {
  box-shadow: ${pixelShadow(colors.accent, 5)};
  color: ${colors.accent};
  transform: scale(1.02);
}
.la-upgrade-flash {
  position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(circle, rgba(255, 211, 107, 0.45), transparent 60%);
  opacity: 0; transition: opacity 0.3s ease-out;
}
.la-upgrade-reroll {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  width: 100%; min-height: 44px; padding: 8px 12px; box-sizing: border-box;
  background: ${colors.barTrack}; color: ${colors.text};
  border: 0; border-radius: 0; box-shadow: ${pixelShadow()};
  font-family: inherit; font-size: inherit;
  cursor: pointer; pointer-events: auto;
  touch-action: manipulation; user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.la-upgrade-reroll:hover, .la-upgrade-reroll:focus-visible {
  box-shadow: ${pixelShadow(colors.accent)}; color: ${colors.accent}; outline: none;
}
.la-upgrade-reroll:disabled {
  opacity: 0.4; cursor: default;
  box-shadow: ${pixelShadow()}; color: ${colors.text};
}
@media (min-width: 768px) {
  .la-upgrade-root { font-size: 12px; }
  .la-upgrade-panel {
    width: auto; align-items: center; gap: 14px; padding: 26px 30px;
  }
  .la-upgrade-title {
    background: none; color: ${colors.accent}; padding: 0;
    font-size: 20px; gap: 10px; letter-spacing: 4px;
  }
  .la-upgrade-subtitle { display: none; }
  .la-upgrade-cards {
    grid-template-columns: repeat(3, minmax(0, 190px)); gap: 14px;
  }
  /* Wireframe v3 (desktop): rarity spelled out in the strip, centered icon
     shrine over name + description, TAKE footer pinned to the bottom. */
  .la-upgrade-card { width: 190px; }
  .la-up-rar { display: inline; }
  .la-up-strip-name, .la-up-strip-key { display: none; }
  .la-up-name { display: block; }
  .la-up-main {
    flex-direction: column; justify-content: center; gap: 10px;
    padding: 14px 12px 10px; text-align: center;
  }
  .la-up-texts { align-items: center; text-align: center; }
  .la-up-desc { font-size: 10px; }
  .la-up-foot {
    display: flex; align-items: center; justify-content: space-between;
    margin-top: auto; padding: 7px 10px; width: 100%; box-sizing: border-box;
    box-shadow: inset 0 2px 0 0 rgba(90, 130, 170, 0.25);
    font-size: 8px; letter-spacing: 2px; color: ${colors.muted};
  }
  .la-upgrade-key { font-size: 10px; }
  .la-upgrade-reroll { width: auto; padding: 10px 14px; }
}
@media (min-width: 1600px) {
  .la-upgrade-root { font-size: 16px; }
  .la-upgrade-title { font-size: 26px; }
  .la-upgrade-cards { grid-template-columns: repeat(3, minmax(0, 240px)); }
  .la-upgrade-card { width: 240px; }
  .la-up-desc { font-size: 13px; }
  .la-upgrade-key { font-size: 13px; }
  .la-up-foot { font-size: 10px; }
}
.la-upgrade-reroll-key { display: none; }
@media (min-width: 768px) {
  .la-upgrade-reroll-key { display: inline; }
}
/* Short landscape phones: tighter chrome; the panel scrolls if needed. */
@media (orientation: landscape) and (max-height: 500px) and (max-width: 1599px) {
  .la-upgrade-panel { gap: 8px; padding: 12px 14px; }
  .la-upgrade-title { font-size: 14px; }
  .la-upgrade-cards { gap: 8px; }
  .la-up-main { padding: 8px 10px; }
}
`;
  document.head.appendChild(style);
}

export class UpgradeOverlay {
  private readonly root: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly flash: HTMLElement;
  private readonly callbacks: UpgradeOverlayCallbacks;
  // Displayed subset of the offer (after rarity filtering) paired with each
  // entry's index in the original offer, which is what onPick reports.
  private display: { choice: UpgradeChoice; index: number }[] = [];
  private open = false;

  constructor(container: HTMLElement, callbacks: UpgradeOverlayCallbacks) {
    ensureUiStyles();
    ensureOverlayStyles();
    this.callbacks = callbacks;

    this.root = document.createElement('div');
    this.root.className = 'la-upgrade-root';
    this.flash = document.createElement('div');
    this.flash.className = 'la-upgrade-flash';
    this.root.appendChild(this.flash);
    this.panel = document.createElement('div');
    this.panel.className = 'la-upgrade-panel';
    this.root.appendChild(this.panel);
    container.appendChild(this.root);
  }

  isOpen(): boolean {
    return this.open;
  }

  /** Show the offer and pause the sim. Re-renderable on reroll while open. */
  show(choices: readonly UpgradeChoice[], rerollsLeft: number, options: UpgradeOverlayShowOptions = {}): void {
    const filter = options.filterRarities;
    this.display = choices
      .map((choice, index) => ({ choice, index }))
      .filter((entry) => !filter || filter.includes(entry.choice.def.rarity));
    if (this.display.length === 0 && choices.length > 0) {
      // Never soft-lock the boon choice: fall back to the unfiltered offer.
      this.display = choices.map((choice, index) => ({ choice, index }));
    }
    if (!this.open) {
      this.open = true;
      this.callbacks.onSetPaused(true);
      window.addEventListener('keydown', this.onKeyDown);
    }
    this.render(rerollsLeft);
    this.root.style.display = 'flex';
    // Appearance flash: quick golden radial pulse.
    this.flash.style.opacity = '1';
    setTimeout(() => {
      this.flash.style.opacity = '0';
    }, 60);
  }

  hide(): void {
    if (!this.open) return;
    this.open = false;
    this.root.style.display = 'none';
    window.removeEventListener('keydown', this.onKeyDown);
    this.callbacks.onSetPaused(false);
  }

  dispose(): void {
    this.hide();
    this.root.remove();
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.open) return;
    if (e.key === '1' || e.key === '2' || e.key === '3') {
      const idx = Number(e.key) - 1;
      if (idx < this.display.length) {
        e.preventDefault();
        this.callbacks.onPick(this.display[idx].index);
      }
    } else if (e.key === 'r' || e.key === 'R') {
      e.preventDefault();
      this.callbacks.onReroll();
    }
  };

  private render(rerollsLeft: number): void {
    this.panel.textContent = '';

    const title = document.createElement('h2');
    title.className = 'la-upgrade-title';
    title.appendChild(icon('sparkles'));
    title.appendChild(document.createTextNode('LUMENSURGE'));
    this.panel.appendChild(title);

    // Tap hint (mobile only — hidden at 768px+ where keys are hinted).
    const subtitle = document.createElement('div');
    subtitle.className = 'la-upgrade-subtitle';
    subtitle.textContent = 'TAP A CARD TO TAKE IT';
    this.panel.appendChild(subtitle);

    const cards = document.createElement('div');
    cards.className = 'la-upgrade-cards';
    this.display.forEach((entry, displayPos) => {
      cards.appendChild(this.makeCard(entry, displayPos));
    });
    this.panel.appendChild(cards);

    const reroll = document.createElement('button');
    reroll.className = 'la-upgrade-reroll';
    reroll.disabled = rerollsLeft <= 0;
    reroll.appendChild(icon('refresh'));
    reroll.appendChild(document.createTextNode('Reroll '));
    const rkey = document.createElement('span');
    rkey.className = 'la-upgrade-reroll-key';
    rkey.textContent = '[R]';
    reroll.appendChild(rkey);
    reroll.appendChild(document.createTextNode(` · ${rerollsLeft} left`));
    // Haptic wrap: iOS switch overlay = a real tap ticks natively and
    // bubbles to this handler. The overlay must not swallow the disabled
    // state — it stays out of the tab order and only mirrors intent.
    const rerollWrap = document.createElement('div');
    rerollWrap.className = 'la-haptic-wrap';
    rerollWrap.appendChild(reroll);
    if (!reroll.disabled) rerollWrap.appendChild(makeHapticSwitch());
    rerollWrap.addEventListener('click', () => {
      if (!reroll.disabled) this.callbacks.onReroll();
    });
    this.panel.appendChild(rerollWrap);
  }

  private makeCard(entry: { choice: UpgradeChoice; index: number }, displayPos: number): HTMLElement {
    const { choice, index } = entry;
    const card = document.createElement('button');
    card.className = `la-upgrade-card rarity-${choice.def.rarity}`;

    // Slim strip: category icon + label left. On phones (wireframe v4) the
    // boon name and the ordinal key join the strip and rarity is conveyed
    // by color alone; on desktop rarity is spelled out on the right.
    const strip = document.createElement('div');
    strip.className = 'la-up-strip';
    const meta = BOON_CATEGORY_META[categorizeUpgrade(choice.def)];
    const cat = document.createElement('span');
    cat.className = 'la-up-cat';
    cat.appendChild(icon(meta.icon));
    cat.appendChild(document.createTextNode(meta.label));
    cat.title = `${meta.label} boon`;
    strip.appendChild(cat);
    const label =
      choice.def.max_rank > 1
        ? `${choice.def.display_name} ${choice.nextRank}`
        : choice.def.display_name;
    const stripName = document.createElement('span');
    stripName.className = 'la-up-strip-name';
    stripName.textContent = label;
    strip.appendChild(stripName);
    const rar = document.createElement('span');
    rar.className = 'la-up-rar';
    rar.textContent = choice.def.rarity.toUpperCase();
    strip.appendChild(rar);
    const stripKey = document.createElement('span');
    stripKey.className = 'la-up-strip-key';
    stripKey.textContent = `${displayPos + 1}`;
    strip.appendChild(stripKey);
    card.appendChild(strip);

    // Main: framed icon shrine + name + description (row on phones,
    // centered column on desktop; the name shows only on desktop).
    const main = document.createElement('div');
    main.className = 'la-up-main';
    const iconBox = document.createElement('div');
    iconBox.className = 'la-up-iconbox';
    // Generated pixel-art boon icon (assets/icons/boons/<slug>.png) replaces
    // the hn-font glyph; a missing PNG falls back to the glyph. The glyph
    // must be hidden once the art loads — the art is alpha-cut, so a glyph
    // left behind bleeds through its transparent pixels.
    const glyph = icon(choice.def.icon);
    iconBox.appendChild(glyph);
    const boonImg = document.createElement('img');
    boonImg.className = 'la-up-iconimg';
    boonImg.alt = '';
    boonImg.onload = () => { glyph.style.visibility = 'hidden'; };
    boonImg.onerror = () => boonImg.remove();
    boonImg.src = `assets/icons/boons/${choice.def.id.replace('upgrade.', '')}.png`;
    if (boonImg.complete && boonImg.naturalWidth > 0) glyph.style.visibility = 'hidden';
    iconBox.appendChild(boonImg);
    main.appendChild(iconBox);
    const texts = document.createElement('div');
    texts.className = 'la-up-texts';
    const name = document.createElement('div');
    name.className = 'la-up-name';
    name.textContent = label;
    texts.appendChild(name);
    const desc = document.createElement('div');
    desc.className = 'la-up-desc';
    desc.textContent = choice.def.description;
    texts.appendChild(desc);
    main.appendChild(texts);
    card.appendChild(main);

    // TAKE footer (desktop only; phones tap the whole card).
    const foot = document.createElement('div');
    foot.className = 'la-up-foot';
    foot.appendChild(document.createTextNode('TAKE'));
    const key = document.createElement('span');
    key.className = 'la-upgrade-key';
    key.textContent = `${displayPos + 1}`;
    foot.appendChild(key);
    card.appendChild(foot);

    // Haptic wrap: the iOS switch overlay sits on top of the card — a real
    // tap toggles it (native system tick) and bubbles here, where we fire
    // the pick. Mouse/keyboard users just get the click.
    const wrap = document.createElement('div');
    wrap.className = 'la-haptic-wrap';
    wrap.appendChild(card);
    wrap.appendChild(makeHapticSwitch());
    wrap.addEventListener('click', () => {
      // Satisfying selection flash, then commit after a beat.
      card.classList.add('selected');
      setTimeout(() => this.callbacks.onPick(index), 140);
    });
    return wrap;
  }
}
